import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useResendNotification } from '@/lib/notifications/use-notifications';
import { CHANNEL_LABEL_KEY } from '@/lib/notifications/labels';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';

// Sending a PNR's current state — the channel vocabulary, the
// re-dispatch status callbacks, and the hook the operation center's
// "next up" primary uses. Lives apart from `pnr-utility-actions.tsx` (which
// renders the buttons) so that file exports only components (react-refresh).

/**
 * Channels the operator can pick for a (re)send — PUSH stays out of the menus.
 *
 * VOICE (N17 / NEX-430) is a Twilio TTS call that speaks the SAME offer the email
 * states — lead, offer summary, and the confirm-within-N-hours ask — and collects
 * the answer as DTMF (1 = accept, 2 = reject) instead of links, because a URL
 * cannot be dictated. It gets its own button rather than a menu row: a phone
 * ringing is a louder act than a message arriving, so it should not be one
 * indistinguishable item in a channel list.
 */
export type ResendChannel = ContactPreferredChannel | 'VOICE';

/**
 * The channels that can be STORED as a passenger's preference (the cases contact
 * write). VOICE is deliberately absent: it is dispatch-only, on operator demand.
 * Making it a stored preference would hand the orchestrator a standing licence to
 * telephone this passenger for every future offer, which is a different product
 * decision from "the agent can call them right now".
 */
export type ContactPreferredChannel = 'SMS' | 'WHATSAPP' | 'EMAIL';

/** The status of a resend operation — success, warning (unavailable), or error. */
export interface ResendStatus {
  tone: 'success' | 'warning' | 'error';
  title: string;
  description: string;
}

/**
 * Fire the re-dispatch and call `onStatus` with the result.
 *
 * `queuedTitle` exists because a queued VOICE dispatch is not "info queued for
 * delivery" — it means a phone is about to ring, which the operator may be
 * standing next to. The channel line reads off `CHANNEL_LABEL_KEY` so the status says
 * "WhatsApp" / "Phone call", not the wire token.
 */
export function queueResend(
  resend: ReturnType<typeof useResendNotification>,
  subCaseUrn: string,
  channel: ResendChannel | undefined,
  t: TFunction<'pnrActions'>,
  opts: { noChannelNote: string; queuedTitle?: string; onQueued?: () => void; onStatus?: (s: ResendStatus) => void },
): void {
  resend.mutate(
    { subCaseUrn, ...(channel ? { channel } : {}) },
    {
      onSuccess: (r) => {
        if (r.status === 'queued') {
          opts.onStatus?.({
            tone: 'success',
            title: opts.queuedTitle ?? t('utility.toast.queued'),
            description: channel
              ? t('utility.toast.channel', { channel: t(CHANNEL_LABEL_KEY[channel]) })
              : opts.noChannelNote,
          });
          opts.onQueued?.();
        } else {
          opts.onStatus?.({
            tone: 'warning',
            title: t('utility.toast.unavailableTitle'),
            description: t('utility.toast.unavailableBody'),
          });
        }
      },
      onError: () =>
        opts.onStatus?.({
          tone: 'error',
          title: t('utility.toast.errorTitle'),
          description: t('utility.toast.errorBody'),
        }),
    },
  );
}

/**
 * Send the current state on the group's preferred channel from outside the utility row
 * — the operation center's "next up" primary while an offer awaits a reply.
 * Same re-dispatch + toasts as the row's "Send info → Preferred channel".
 */
export function useResendOffer(group: DisruptionPnrGroupRow): { run: () => void; isPending: boolean } {
  const { t } = useTranslation('pnrActions');
  const resend = useResendNotification(group.caseUrn);
  return {
    run: () =>
      queueResend(resend, group.subCaseUrn, undefined, t, {
        noChannelNote: t('utility.toast.preferredChannelNote'),
      }),
    isPending: resend.isPending,
  };
}
